import { getByPath, hasTemplateDeep, parse } from '@de/utils/interpolation';
import { isInjectedRouteContext } from '@de/utils/interpolation/injectedRouteMarker';
import { isPublishedScopeContext } from '@de/utils/published-scope/unpublishedScope';
import type { DataSourceDeclaration } from '@de/providers/DataSourceManager.types';
import { isDerivedRoot } from './derivedRoots';
import { resolveDataSourceType } from './source-types';

/**
 * Roots that are NOT treated as a deferrable dependency. `context.*` is the
 * host-supplied flow context — it is present at mount and existing sources
 * legitimately interpolate possibly-empty `{{context.*}}` values without
 * wanting their fetch deferred. `topology.*` is the same kind of root: the
 * projection is present when the source runs, and a missing block is a
 * source error rather than a deferred fetch. Only `formState.*` and
 * sibling-source roots gate execution (a selection that has not happened
 * yet, or an upstream source that has not resolved).
 */
const NON_DEPENDENCY_ROOTS: ReadonlySet<string> = new Set([
  'context',
  'topology',
]);

/**
 * Conditional-execution guard for the DataSourceManager.
 *
 * Returns `true` when `decl` has a **templated** URL-shaping request field whose
 * `{{formState.*}}` / sibling-source token resolves to an empty value
 * (`undefined`, `null`, or `''`) against `interpolationContext`. The DSM
 * uses this to keep a selection-driven source `pending` instead of firing
 * a request with an empty interpolation (e.g. `GET /v1/offerings/` before
 * a result is selected).
 *
 * Design constraints (mirrors `declarationReferencesFormKey` in
 * `DataSourceManager`):
 *   - **Templated-only.** A decl with no `{{…}}` in URL-shaping fields is
 *     never skipped (`hasTemplateDeep` confirmation), so static endpoints
 *     always fetch.
 *   - **Which fields count is the source type's call.** This module asks the
 *     registered descriptor and does not know one type from another. HTTP
 *     narrows to `endpoint` and `headers`, and to query after empty
 *     `formState` tokens are omitted, since those optional facets must not
 *     defer a fetch. An unresolved optional body criterion is dropped
 *     harmlessly by JSON serialization. A type may also name fields that
 *     must resolve regardless.
 *   - **Dependency roots only.** `context.*` and `topology.*` never defer a
 *     fetch; only `formState.*` and sibling-source roots do. This keeps
 *     existing context-driven sources fetching exactly as before. A root a
 *     source type derives for the run (see `derivedRoots`) is not a
 *     dependency either.
 *   - **String-empty semantics.** Only `undefined` / `null` / `''` count as
 *     unresolved. An empty array / object resolved from an upstream source
 *     is a legitimate value and does NOT defer the fetch.
 *   - **Never throws.** Parse failures (malformed templates surface at
 *     publish time) are treated as "no unresolved dependency" so the guard
 *     can never block a fetch on a parser edge case.
 *
 * @param decl - The data-source declaration about to run.
 * @param interpolationContext - The merged `{ <source>, context, formState, topology }`
 *   context `runSource` builds before deriving the cache key / fetching.
 */
export function hasUnresolvedDependency(
  decl: DataSourceDeclaration,
  interpolationContext: unknown,
): boolean {
  const fields: unknown[] = [
    ...getDependencyCheckFields(decl, interpolationContext),
  ];
  const requiredBodyFields = getRequiredBodyDependencyFields(decl);
  if (requiredBodyFields.length > 0) {
    fields.push(...requiredBodyFields);
  }

  // Static (template-free) declarations are never deferred.
  if (!fields.some((value) => hasTemplateDeep(value))) {
    return false;
  }

  return fields.some((value) =>
    fieldHasUnresolvedDependency(value, interpolationContext),
  );
}

function fieldHasUnresolvedDependency(
  value: unknown,
  context: unknown,
): boolean {
  if (typeof value === 'string') {
    return stringHasUnresolvedDependency(value, context);
  }
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => fieldHasUnresolvedDependency(item, context));
  }
  return Object.values(value as Record<string, unknown>).some((item) =>
    fieldHasUnresolvedDependency(item, context),
  );
}

function stringHasUnresolvedDependency(
  template: string,
  context: unknown,
): boolean {
  let parsed;
  try {
    parsed = parse(template);
  } catch {
    return false;
  }

  for (const segment of parsed.segments) {
    if (segment.type !== 'expression' || segment.kind !== 'path') {
      continue;
    }
    const root = segment.segments[0];
    if (root === undefined || root.type !== 'key') {
      continue;
    }
    if (
      NON_DEPENDENCY_ROOTS.has(root.name) ||
      isDerivedRoot(context, root.name)
    ) {
      continue;
    }
    // A mounted provider already resolved this root. Null and a missing
    // key are values (null, or the unpublished-scope sentinel), not a
    // sibling source that has not loaded. A data source of the same name
    // stays a real dependency.
    if (
      (root.name === 'scope' && isPublishedScopeContext(context)) ||
      (root.name === 'route' && isInjectedRouteContext(context))
    ) {
      continue;
    }
    const resolved = getByPath(context, segment.segments);
    if (isUnresolvedValue(resolved)) {
      return true;
    }
  }
  return false;
}

function isUnresolvedValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  // Whitespace-only counts as unresolved — an empty/blank interpolation
  // (e.g. `{{formState.selectedOfferingId}}` before a selection) commonly
  // resolves to `''` or a stray space, mirroring `isNonEmptyString` in
  // `isSubmittableResult`. Non-string values (numbers, objects, arrays —
  // including empty ones) are legitimate resolved values and never defer.
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  return false;
}

/**
 * Fields scanned for unresolved interpolation tokens before a fetch runs.
 *
 * Which fields those are is the source type's answer, not this module's. HTTP
 * narrows to URL-shaping fields; the types whose whole body is the request
 * scan everything they interpolate. An unregistered kind scans nothing, and
 * is refused at dispatch rather than here.
 */
function getDependencyCheckFields(
  decl: DataSourceDeclaration,
  context: unknown,
): readonly unknown[] {
  const sourceType = resolveDataSourceType(decl);
  if (!sourceType) {
    return [];
  }
  return (
    sourceType.dependencyFields?.(decl, context) ??
    sourceType.interpolatableFields(decl)
  );
}

/**
 * Fields a source type requires resolved on top of its dependency fields,
 * for the case where the type's general rule is wrong for one operation.
 */
function getRequiredBodyDependencyFields(
  decl: DataSourceDeclaration,
): readonly unknown[] {
  return resolveDataSourceType(decl)?.requiredResolvedFields?.(decl) ?? [];
}
